import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {
    getCertificate,
    getProviderConnectionCapabilities,
    listCertificates,
    listProviderConnectionsForCertificates,
} from './queries';

import {
    deleteCertificate,
    requestCertificate,
} from './mutations';

import {
    Certificate,
    CertificateConnectionResult,
    DeleteCertificate,
    DeleteCertificateInput,
    DeleteCertificateResult,
    GetCertificate,
    ListCertificates,
    RequestCertificate,
    RequestCertificateInput,
} from '../types';

const client = generateClient();

// FNP-1193 — the certificates service.
//
// ⚠️ ERRORS ARE RE-THROWN, NEVER SWALLOWED. Catching and returning undefined
// makes a failed fetch indistinguishable from a tenant with no certificates:
// `isError` stays false and the table renders its empty state, so "the API is
// down" and "you have no certificates" look identical. Those are two of the
// three states this module most needs to tell apart.
//
// On the WRITE path the reason is sharper. A refused request carries the
// provider's own words — an AccessDenied naming `acm:RequestCertificate` above
// all — and that refusal is the most valuable thing the call produced. The
// mutation hook classifies it; stripping it here would leave nothing to
// classify and the tenant would be told "something went wrong" about a problem
// with a precise fix.

/**
 * What a connection declares about writing, as this module needs it.
 *
 * ⚠️ FNP-1280 — TWO DECLARATIONS, NOT ONE, BECAUSE THEY ARE TWO IAM GRANTS.
 * This used to carry `writable` alone, on the reasoning that the backend
 * gated certificates on it — which it did, and that was the defect.
 * `flexi-domains-api/src/certificate/src/main.ts` built its issuer with
 * `{canRequest: connection.writable === true, canDelete: <the same value>}`,
 * so a tenant who granted `route53:ChangeResourceRecordSets` and not
 * `acm:RequestCertificate` — independent statements in one role — could not
 * be represented, and was offered a control that failed at the provider.
 *
 * The backend now reads `connection.certificateCapabilities`, and this mirrors
 * that. See queries.ts for why the other obvious alternative
 * (`canWriteRecords`) would also have been a proxy for the wrong thing.
 */
export type CertificateWriteCapabilities = {
    // ⚠️ THE DNS DECLARATION, AND SINCE FNP-1280 IT NO LONGER GATES
    // CERTIFICATES. It is still carried on this path because requesting a
    // certificate also PUBLISHES its validation record through the same
    // connection's DNS credential — a connection that can issue but cannot
    // write DNS produces a certificate stuck in VALIDATION_NOT_WRITTEN.
    writable: boolean,
    // ⚠️ FNP-1280 — THE ISSUER DECLARATION. A separate grant, so a separate
    // field: the tenant writes `route53:ChangeResourceRecordSets` and
    // `acm:RequestCertificate` as independent statements in their role.
    //
    // ⚠️ DECLARED BY THE TENANT, NOT PROBED. True means "the tenant said this
    // role can request", not "this role provably can". A request may still be
    // refused at ACM, and that refusal now arrives classified.
    canRequestCertificates: boolean,
    canDeleteCertificates: boolean,
}

/** A connection as the REQUEST FORM's picker needs it. */
export type CertificateConnection = {
    id: string,
    // What the tenant named it. The picker shows this, never the opaque id.
    displayName: string,
    // The catalog provider id, e.g. 'ROUTE53'.
    providerId: string,
    // ⚠️ Only 'ACTIVE' may be offered. Any other status is the provider saying
    // the credential does not work NOW, whatever was declared at connect time.
    status: string,
    // ⚠️ NON-NULL ON THE SCHEMA (`Boolean!`), defaulted false server-side —
    // "unknown" is deliberately not representable, because a client reading
    // unknown as "probably yes" is how a control appears in front of a user
    // that fails after they filled in a form.
    writable: boolean,
    // ⚠️ FNP-1280 — WHAT THE PICKER ACTUALLY FILTERS ON. `writable` above is
    // the DNS grant and used to stand in for this one; a tenant with DNS write
    // and no ACM write was offered a connection that would refuse them.
    canRequestCertificates: boolean,
}

// The certificate declaration as it arrives. Non-null on the schema, but typed
// optional here for the same reason `writable` is: a partial response must not
// be able to read as permission.
type CertificateCapabilitiesResponse = {
    canRequest?: boolean | null,
    canDelete?: boolean | null,
} | null

type GetProviderConnectionCapabilities = {
    getProviderConnection?: {
        id: string,
        status: string,
        writable?: boolean | null,
        certificateCapabilities?: CertificateCapabilitiesResponse,
    } | null
}

type ListProviderConnections = {
    listProviderConnections?: {
        items?: {
            id: string,
            displayName: string,
            providerId: string,
            status: string,
            writable?: boolean | null,
            certificateCapabilities?: CertificateCapabilitiesResponse,
        }[] | null,
        nextToken?: string | null,
    } | null
}

/**
 * One page of THIS TENANT'S certificates.
 *
 * ⚠️ NO TENANT ARGUMENT IS SENT. `listCertificates` takes only `limit` and
 * `nextToken`; the tenant is a key condition built server-side from the
 * caller's own claim. There is nothing to pass and nothing to get wrong.
 *
 * ⚠️ EVERY ITEM'S `status` WAS READ THROUGH TO THE PROVIDER to answer this
 * call. That is the accepted N+1 (bounded by certificates-per-tenant, a number
 * in the tens) and it must not be "optimized" into a cached projection —
 * doing so would make `status` a value that can disagree with the provider.
 */
export const fetchCertificates = async (
    limit: number,
    nextToken: string | null,
): Promise<CertificateConnectionResult> => {

    const response = await client.graphql<GraphQLQuery<ListCertificates>>({
        query: listCertificates,
        variables: {
            limit,
            nextToken,
        },
    });

    return {
        items: response.data?.listCertificates?.items ?? [],
        // ⚠️ `?? null` and NOT `|| null`: coercing an empty-string token to
        // null would end pagination early.
        nextToken: response.data?.listCertificates?.nextToken ?? null,
    };
};

/** One certificate, by Flexi's own id. */
export const fetchCertificate = async (
    id: string,
): Promise<Certificate | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetCertificate>>({
        query: getCertificate,
        variables: {id},
    });

    return response.data?.getCertificate ?? undefined;
};

/**
 * The connections a certificate may be requested through.
 *
 * Returns them ALL, unfiltered — the filtering rule lives in the hook next to
 * its own test, because "which connections may be offered" is a decision worth
 * testing directly rather than through a network mock.
 */
export const fetchConnections = async (): Promise<CertificateConnection[]> => {

    const response = await client.graphql<GraphQLQuery<ListProviderConnections>>({
        query: listProviderConnectionsForCertificates,
        variables: {limit: 100},
    });

    const items = response.data?.listProviderConnections?.items ?? [];

    return items.map(item => ({
        id: item.id,
        displayName: item.displayName,
        providerId: item.providerId,
        status: item.status,
        // Explicitly `=== true`: the field is non-null on the schema, but a
        // partial response must not read as permission.
        writable: item.writable === true,
        // FNP-1280 — same rule, one level deeper. The BLOCK may be absent on a
        // partial response even though the schema says it cannot be, so this
        // reads through an optional chain and lands on false either way.
        canRequestCertificates: item.certificateCapabilities?.canRequest === true,
    }));
};

/**
 * What this connection's credential can do, as this module needs it.
 *
 * Undefined means "could not be determined", and every caller treats that as
 * NOT permitted.
 *
 * ⚠️ A CONNECTION THAT IS NOT ACTIVE REPORTS NOTHING, whatever its stored
 * snapshot says. The snapshot records what the credential proved AT CONNECT
 * TIME; an INVALID or UNREACHABLE status is the provider saying that credential
 * does not work NOW.
 */
export const fetchConnectionCapabilities = async (
    connectionId: string,
): Promise<CertificateWriteCapabilities | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetProviderConnectionCapabilities>>({
        query: getProviderConnectionCapabilities,
        variables: {id: connectionId},
    });

    const connection = response.data?.getProviderConnection;

    if (!connection) {
        return undefined;
    }

    if (connection.status !== 'ACTIVE') {
        return undefined;
    }

    return {
        // Explicitly `=== true`: a null or absent value is not permission.
        writable: connection.writable === true,
        // FNP-1280 — the two ACM grants, read separately from each other and
        // from the DNS one. Same `=== true` rule through an optional chain.
        canRequestCertificates: connection.certificateCapabilities?.canRequest === true,
        canDeleteCertificates: connection.certificateCapabilities?.canDelete === true,
    };
};

/**
 * Request a certificate, and write its validation record into the owning zone.
 *
 * ⚠️ THE RESULT IS NULLABLE ON THE SCHEMA and that is not an oversight: a
 * non-null field resolving null nulls its whole parent on this platform, so the
 * honest failure mode is an error entry plus null rather than a fabricated
 * certificate. `undefined` here means the request reported nothing, and the
 * caller must not invent a certificate for it.
 *
 * ⚠️ A RETURNED CERTIFICATE IS NOT AN ISSUED ONE. It comes back
 * PENDING_VALIDATION at best. Read `status` before telling anyone anything.
 */
export const requestNewCertificate = async (
    input: RequestCertificateInput,
): Promise<Certificate | undefined> => {

    const response = await client.graphql<GraphQLQuery<RequestCertificate>>({
        query: requestCertificate,
        variables: {input},
    });

    return response.data?.requestCertificate ?? undefined;
};

/**
 * Delete a certificate.
 *
 * ⚠️ THE PROVIDER DECIDES, AND A REFUSAL IS NOT AN ERROR HERE.
 *
 * An in-use refusal comes back as a RESOLVED result with `deleted: false` and
 * `inUse: true` — not as a thrown GraphQL error. So this resolving successfully
 * proves nothing about whether the certificate is gone, and every caller must
 * read `deleted` rather than treating a resolution as success.
 *
 * Flexi does NOT pre-check against local state before calling. Local state goes
 * stale, the check would need its own network call to be worth anything, and it
 * would make this code the authority on a rule the provider owns.
 */
export const removeCertificate = async (
    input: DeleteCertificateInput,
): Promise<DeleteCertificateResult | undefined> => {

    const response = await client.graphql<GraphQLQuery<DeleteCertificate>>({
        query: deleteCertificate,
        variables: {input},
    });

    return response.data?.deleteCertificate ?? undefined;
};
